import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Select,
  MenuItem,
  Tab,
  Tabs,
  IconButton
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import QueryParams from './QueryParamsComponent';
import AuthorizationComponent from '../Collections/AuthorizationComponent';
import HeadersComponent from './HeadersComponent.jsx';
import BodyComponent from './BodyComponent';
import { useDispatch, connect, useSelector } from 'react-redux';
import { renameTab } from '../../redux-store/tabSlice';
import { updateState } from '../../redux-store/currentUserSlice.js';
import {
  sendApiCall,
  updateEntityLocalAgentResponse,
  updateRequest,
} from '../../redux-store/currentUserActions';
import {
  updateEntity,
  updateEntityNameFromUrl,
} from '../../redux-store/currentUserSlice';
import LoadingOverlay from '../Common/LoadingOverlay';
import AppContext from '../../AppContext';
import { useGuestUser } from '../../contexts/GuestUserContext.js';
import CTAAccountDialog from '../Common/CTAAccountDialog.js';
import ConditionalRender from '../../utils/conditionalRender';
import SaveIcon from '@mui/icons-material/Save';
import { updateResourceSummary } from "../../redux-store/currentUserActions";
import TextFieldWithVariableTooltip from 'components/Common/TextFieldWithVariableTooltip';
import SwaggerDoc from './SwaggerDoc';
import DynamicVariables from './DynamicVariables';
import { getAllDynamicVariables, createDynamicVariable, updateDynamicVariable, deleteDynamicVariable } from 'redux-store/dynamicVariableReducers/dynamicVariableActions';
import { useSnackbar } from 'contexts/CustomSnackbarContext';
import { callApiThroughExtension } from '../../utils/extensionUtils';
import { setIsSaveRequest } from 'redux-store/variableSetReducers/variableSetSlice';

const ApiComponent = ({ entity, onEntitySlugUpdate, selectedSidebarState, selectedEnvironmentId, isSaveRequest }) => {
  const selectedSummary = useSelector((state) => state.user.selectedSummary);
  const dispatch = useDispatch();
  const { openSnackbar } = useSnackbar();
  const [authorization, setAuthorization] = useState({});
  const [method, setMethod] = useState('');
  const [url, setUrl] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [queryParams, setQueryParams] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [body, setBody] = useState([]);
  const [bodyType, setBodyType] = useState([]);
  const [openAccountDialog, setOpenAccountDialog] = useState(false);
  const { isGuestUser } = useGuestUser();
  const { setSendClicked } = useContext(AppContext);
  const [swaggerDoc, setSwaggerDoc] = useState(null);
  const [dynamicVariables, setDynamicVariables] = useState([]);
  // const tabLabels = ['Params', 'Authorization', 'Headers', 'Body', 'Settings'];
  const tabLabels = ['Params', 'Authorization', 'Headers', 'Body', 'Swagger', 'Post Script'];
  const defaultHeaders = [
    { name: 'Accept', value: '*/*', required: true, datatype: "string" },
    { name: 'Accept-Encoding', value: 'identity', required: true, datatype: "string" },
    { name: 'Connection', value: 'keep-alive', required: true, datatype: "string" },
  ];
  useEffect(() => {
    setAuthorization({
      authorization_type: selectedSummary?.authorization_type,
      authorization_value: selectedSummary?.authorization_value
    })
  }, [selectedSummary])

  useEffect(() => {
    const request = entity?.request;
    if (Array.isArray(request?.body) && request.body.length > 0) {
      setActiveTab(3);
    } else if (typeof request?.body === 'object' && request.body !== null && Object.keys(request.body).length > 0) {
      setActiveTab(3);
    } else if (typeof request?.body == 'string' && request?.body?.length > 0) {
      setActiveTab(3);
    } else {
      setActiveTab(0);
    }
    setUrl(request?.url || '');
    setMethod(request?.request_type || '');
    setBody(request?.body);
    setBodyType(request?.body_type);
    setHeaders(getMergedHeaders());
    setQueryParams(request?.params);
    setSwaggerDoc(request?.swagger_doc || null);
    getDynamicVariables();
  }, [selectedSidebarState, entity, dispatch]);

  const getDynamicVariables = async () => {
    const response = await dispatch(getAllDynamicVariables({
      resourceId: entity?.request?.entity_id,
      resourceType: "Entity"
    }));
    setDynamicVariables(response?.payload?.dynamic_variables);
  }

  const getMergedHeaders = () => {
    const backendHeadersArray = entity?.request?.headers || [];
    const uniqueDefaultHeaders = defaultHeaders.filter(
      (defaultHeader) =>
        !backendHeadersArray.some(
          (backendHeader) => backendHeader.name === defaultHeader.name
        )
    );
    const mergedHeaders = [...uniqueDefaultHeaders, ...backendHeadersArray];
    return mergedHeaders;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleMethodChange = (event) => {
    const newMethod = event.target.value;
    setMethod(newMethod);
  };

  const handleUrlChange = (event) => {
    const extractedParams = updateParamsFromUrl(event.target.value);
    setUrl(event.target.value);
    setQueryParams(prevState => {
      const formattedParams = extractedParams.map((param, index) => {
        if ((prevState[index] && param) && (prevState[index]?.name === param?.name)) {
          return { ...prevState?.[index], name: param?.name, value: param?.value };
        } else {
          return { ...(param || {}) };
        }
      });
      return formattedParams;
    });
  };

  const updateParamsFromUrl = (requestUrl) => {
    if (!requestUrl) {
      return [];
    }
    try {
      const indexOfQueryStart = requestUrl.indexOf('?');
      if (indexOfQueryStart !== -1) {
        const queryString = requestUrl.substring(indexOfQueryStart + 1);
        const params = new URLSearchParams(queryString);
        const extractedParams = Array.from(params.entries()).map(
          ([key, value]) => ({ name: key, value, description: "", required: false, datatype: "" })
        );
        return extractedParams;
      }
      return [];
    } catch (error) {
      console.error('Error parsing URL parameters', requestUrl, error);
      return [];
    }
  };

  const concatQueryParams = (rows) => {
    const tmpUrl = url || entity?.request?.url || '';
    const validParams = rows.filter((p) => p.name);
    const tParams = buildQueryParams(validParams);
    const indexOfQuery = tmpUrl.indexOf('?');
    const baseUrl =
      indexOfQuery > -1 ? tmpUrl.substring(0, indexOfQuery) : tmpUrl;
    const newUrl = `${baseUrl}${tParams.length ? '?' + tParams : ''}`;
    setUrl(newUrl);
    setQueryParams(validParams);
  };

  const buildQueryParams = (arr) => {
    return arr
      .map((item) => (item.value ? `${item.name}=${item.value}` : item.name))
      .join('&')
      .replace(/&+$/, '');
  };

  const handleSaveRequest = async () => {
    const request = entity?.request;
    if (request) {
      const state = {
        id: request?.id,
        request: {
          url: url,
          request_type: method,
          params: queryParams,
          headers: headers,
          body: body,
          body_type: bodyType,
        },
        use_local_agent: localStorage.getItem("runOn") === "localAgent"
      };
      dispatch(updateRequest(state));

      await dispatch(updateResourceSummary({
        id: selectedSummary?.id,
        summary: authorization
      }));
    }
  };

  const handleSendRequest = async () => {
    if (isGuestUser) {
      handleOpenAccountDialog();
      return;
    }
    setIsLoading(true);
    setSendClicked(true);
    // await new Promise(resolve => setTimeout(resolve, 250));
    await handleSaveRequest();
    // await new Promise(resolve => setTimeout(resolve, 250));
    let res;
    if (localStorage.getItem('runOn') === 'cloudAgent') {
      res = await dispatch(sendApiCall({ id: selectedSidebarState?.id, selectedEnvironmentId: selectedEnvironmentId }));
    } else {
      const requestJson = {
        url: entity?.request?.request_json?.url,
        request_type: entity?.request?.request_json?.request_type,
        params: entity?.request?.request_json?.params,
        headers: entity?.request?.request_json?.headers,
        body: entity?.request?.request_json?.body,
        body_type: entity?.request?.request_json?.body_type,
        authorization_type: entity?.request?.request_json?.authorization_type,
        authorization_value: entity?.request?.request_json?.authorization_value,
      }
      try {
        res = await callApiThroughExtension(requestJson);
        const localUpdatedResponse = await dispatch(updateEntityLocalAgentResponse({
          entityId: selectedSidebarState?.id,
          updateData: res,
        }));
      } catch (error) {
        console.error("Extension API call error:", error);
        openSnackbar({ message: error?.error || error?.toString() || "Failed to fetch. Please check your URL and try again.", severity: "error" });
        setIsLoading(false);
        return;
      }

    }

    if (res?.payload?.response) {
      // dispatch(setResponse({ apiId: selectedSidebarState?.id, response: res?.payload?.response }));
      const name = res.payload?.name;
      const slug = res.payload?.slug;
      await dispatch(
        updateEntity({ id: selectedSidebarState?.id, name: name, slug: slug })
      );
      await dispatch(
        updateEntityNameFromUrl({ id: selectedSidebarState?.id, name: name })
      );
      await dispatch(renameTab({ id: selectedSidebarState?.id, newName: name }))
      const entity = res.payload;
      const newState = entity?.folder_id ? { folderApi: entity } : { collectionApi: entity };
      await dispatch(updateState(newState));
      onEntitySlugUpdate(slug);
    }
    setIsLoading(false);
  };

  const handleParamsChange = (newParams) => {
    concatQueryParams(newParams);
  };

  const handleHeadersChange = (validHeaders) => {
    setHeaders(validHeaders);
  };

  const handleBodyChange = (body, bodyType) => {
    setBody(body);
    setBodyType(bodyType);
  };

  const getTabStyle = (index) => ({
    textTransform: 'none',
    color: activeTab === index ? 'var(--text-primary)' : 'var(--text-secondary)',
    backgroundColor: activeTab === index ? 'var(--primary-color)' : 'var(--background-primary)',
  });

  const handleOpenAccountDialog = () => {
    setOpenAccountDialog(true);
  };

  const handleCloseAccountDialog = () => {
    setOpenAccountDialog(false);
  };

  const handleSaveDynamicVariable = async (varName, varValueEvaluator) => {
    if (varName.trim() && varValueEvaluator.trim()) {
      const response = await dispatch(createDynamicVariable({
        varName: varName,
        varValueEvaluator: varValueEvaluator || "",
        resourceType: "Entity",
        resourceId: entity?.request?.entity_id
      }));
      if (response?.payload) {
        openSnackbar({ message: "Post Script Variable Saved Successfully!", severity: "success" })
        getDynamicVariables();
      }
    }
  }

  const handleUpdateDynamicVariable = async (varId, varName, varValueEvaluator) => {
    const response = await dispatch(updateDynamicVariable({
      dynamicVariableId: varId,
      dynamicVariableValue: varValueEvaluator || "",
    }));
    if (response?.payload) {
      openSnackbar({ message: "Post Script Variable Updated Successfully!", severity: "success" })
      getDynamicVariables();
    }
  }

  const handleDeleteDynamicVariable = async (varId) => {
    const response = await dispatch(deleteDynamicVariable({
      dynamicVariableId: varId,
    }));
    if (response?.payload) {
      openSnackbar({ message: "Post Script Variable Deleted Successfully!", severity: "success" })
      getDynamicVariables();
    }
  }

  useEffect(() => {
    const updateSaveRequest = async () => {
      setSendClicked(true);
      await handleSaveRequest();
      await dispatch(setIsSaveRequest(false));
    }

    if (isSaveRequest) updateSaveRequest();
  }, [isSaveRequest]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <QueryParams
            skipOutsideClickCheck
            initialParams={queryParams}
            onParamsChange={handleParamsChange}
          />
        );
      case 1:
        return (
          <AuthorizationComponent
            allowInheritFromParent={true}
            authorization={authorization}
            onAuthorizationUpdate={setAuthorization}
          />
        );
      case 2:
        return (
          <HeadersComponent
            skipOutsideClickCheck
            headers={headers}
            onHeadersChange={handleHeadersChange}
          />
        );
      case 3:
        return (
          <BodyComponent
            url={url}
            body={body}
            headers={headers}
            bodyType={bodyType}
            onBodyChange={handleBodyChange}
          />
        );
      case 4:
        return (
          <SwaggerDoc swaggerDoc={swaggerDoc} />
        )
      case 5:
        return (
          <DynamicVariables
            initialDynamicVariables={dynamicVariables}
            handleSaveDynamicVariable={handleSaveDynamicVariable}
            handleUpdateDynamicVariable={handleUpdateDynamicVariable}
            handleDeleteDynamicVariable={handleDeleteDynamicVariable}
            responseVariables={entity?.request?.response?.body || {}}
          />
        )
      default:
        return null;
    }
  };

  const tabStyle = {
    height: '30px',
    minHeight: '30px',
    textTransform: 'none',
    color: 'var(--text-primary)',
    backgroundColor: 'transparent',
    fontSize: "11px",
  };

  const commonRequestTypeStyle = {
    fontWeight: 600,
    fontSize: "12px",
  }

  const getColorByMethod = (method) => {
    switch (method) {
      case "GET":
        return "green";
      case "POST":
        return "#ffb900";
      case "PUT":
        return "#3b82f6";
      case "PATCH":
        return "#ff24ff";
      case "DELETE":
        return "red";
      case "HEAD":
        return "green";
      case "OPTIONS":
        return "#a04cfc";
      default:
        return "black";
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        marginLeft: '10px',
        marginRight: '10px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Select
          size="small"
          value={method}
          onChange={handleMethodChange}
          sx={{
            marginRight: '10px',
            width: '15%',
            fontWeight: "600",
            fontSize: "12px",
            color: getColorByMethod(method),
            '& .MuiSelect-select': {
              color: getColorByMethod(method),
            },
          }}
        >
          <MenuItem value="GET" sx={{ color: "green", ...commonRequestTypeStyle }}>
            GET
          </MenuItem>
          <MenuItem value="POST" sx={{ color: '#ffb900', ...commonRequestTypeStyle }}>
            POST
          </MenuItem>
          <MenuItem value="PUT" sx={{ color: '#3b82f6', ...commonRequestTypeStyle }}>
            PUT
          </MenuItem>
          <MenuItem value="PATCH" sx={{ color: '#ff24ff', ...commonRequestTypeStyle }}>
            PATCH
          </MenuItem>
          <MenuItem value="DELETE" sx={{ color: 'red', ...commonRequestTypeStyle }}>
            DELETE
          </MenuItem>
          <MenuItem value="HEAD" sx={{ color: 'green', ...commonRequestTypeStyle }}>
            HEAD
          </MenuItem>
          <MenuItem value="OPTIONS" sx={{ color: '#a04cfc', ...commonRequestTypeStyle }}>
            OPTIONS
          </MenuItem>
        </Select>
        <TextFieldWithVariableTooltip
          value={url}
          onChange={handleUrlChange}
          variant="outlined"
          size="small"
          fullWidth
          placeholder="Enter request url or paste curl"
          InputProps={{
            sx: {
              fontWeight: "400", fontSize: "12px",
            }
          }}
          sx={{ marginRight: '10px' }}
        />

        <Button
          variant="contained"
          onClick={handleSendRequest}
          size="medium"
          endIcon={<ArrowDropDownIcon />}
          style={{
            width: '120px', // Set a fixed width for the button
            fontWeight: "500",
            fontSize: "12px",
            textTransform: "none",
          }}
          sx={{ marginLeft: '10px' }}
        >
          Send
        </Button>

        <ConditionalRender if={entity?.can_update}>
          <Button
            variant="text"
            onClick={handleSaveRequest}
            size="medium"
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '80px', // Set a fixed width for the button
              fontWeight: "500",
              fontSize: "11px",
              textTransform: "none",
              color: 'var(--text-secondary)',
            }}
          >
            <IconButton
              onClick={handleSaveRequest}
              size="small"
              style={{
                padding: '0',
                marginRight: '5px',
              }}
            >
              <SaveIcon fontSize="small" color='var(--text-secondary)' /> {/* Set icon size */}
            </IconButton>
            Save
          </Button>
        </ConditionalRender>

      </Box>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        sx={{
          marginTop: '10px',
          height: '30px',
          minHeight: '30px',
          border: '1px solid var(--borderColor)',
          borderRadius: '5px',
        }}
      >
        {tabLabels.map((label) => (
          <Tab
            key={label}
            label={label}
            sx={getTabStyle(label)}
            style={tabStyle}
          />
        ))}
      </Tabs>
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          marginTop: '10px',
          marginBottom: '0px',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {renderTabContent()}
      </Box>
      <LoadingOverlay isLoading={isLoading} />
      <CTAAccountDialog
        open={openAccountDialog}
        handleClose={handleCloseAccountDialog}
      />
    </Box>
  );
};

function mapStateToProps(state, ownProps) {
  return {
    ...ownProps,
    selectedSidebarState: state.user.selectedSidebarState,
    isSaveRequest: state.variableSet.isSaveRequest
  }
}

export default connect(mapStateToProps)(ApiComponent);
